import { useState, useEffect } from 'react';
import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc, getDocFromCache, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { loadProfile } from '../utils/loadProfile';
import { clearSessionCache, writeSessionHint } from '../utils/sessionCache';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const userRef = doc(db, 'users', firebaseUser.uid);
          const { profile, fromCache } = await loadProfile(
            () => getDocFromCache(userRef),
            () => getDoc(userRef)
          );

          // Firebase User 객체와 프로필 데이터 병합
          // 주의: Firebase User 객체는 읽기 전용 속성이 많으므로 필요한 정보만 추출하거나
          // 별도 상태로 관리하는 것이 좋지만, 여기서는 편의상 객체에 속성을 추가하는 방식으로 처리
          const merged = profile ? { ...firebaseUser, ...profile } : firebaseUser;
          setUser(merged);
          // 다음에 공유로 열릴 때 로그인 확인을 기다리지 않고 쓸 사본 (utils/sessionCache)
          writeSessionHint(merged);

          // 기기 사본으로 열었으면 서버 값을 뒤에서 확인한다. 앱이 쓰는 값(온보딩 여부)이
          // 달라졌을 때만 고친다 — 같으면 앱 전체를 다시 그릴 이유가 없다
          if (fromCache) {
            getDoc(userRef).then(snap => {
              if (!snap.exists() || auth.currentUser?.uid !== firebaseUser.uid) return;
              const fresh = snap.data();
              if (fresh.isOnboardingCompleted !== profile.isOnboardingCompleted) {
                const updated = { ...firebaseUser, ...fresh };
                setUser(updated);
                writeSessionHint(updated);
              }
            }).catch(() => { /* 오프라인이면 기기 사본 그대로 */ });
          }
        } catch (err) {
          // 프로필을 못 읽었을 뿐 로그인은 되어 있다. 사본은 건드리지 않는다
          console.error('Error fetching user profile:', err);
          setUser(firebaseUser);
        }
      } else {
        setUser(null);
        clearSessionCache();
      }
      setLoading(false);
      setError(null);
    });

    return () => unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    try {
      setLoading(true);
      setError(null);

      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // 로그인 성공 시 프로필 확인/생성
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        // 새 사용자라면 기본 프로필 생성 (온보딩 미완료 상태)
        await setDoc(userRef, {
          email: user.email,
          createdAt: new Date().toISOString(),
          isOnboardingCompleted: false
        });
      }

      console.log('Login successful:', user.email);
      return user;
    } catch (error) {
      console.error('Google sign in error:', error);

      let errorMessage = '로그인 중 오류가 발생했습니다.';
      if (error.code === 'auth/popup-blocked') {
        errorMessage = '팝업이 차단되었습니다. 팝업 차단을 해제해주세요.';
      } else if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = '로그인이 취소되었습니다.';
      } else if (error.code === 'auth/configuration-not-found') {
        errorMessage = 'Firebase 설정이 올바르지 않습니다.';
      }

      setError({ code: error.code, message: errorMessage });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      setError(null);
      await signOut(auth);
      console.log('Logout successful');
    } catch (error) {
      console.error('Logout error:', error);
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { user, loading, error, signInWithGoogle, logout };
};